import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../../utils/hooks';
import { useStoreContext } from '../../../store/store';
import { IEmailLogEntry } from '../../../utils/constants/interfaces';
import { Helpers } from '../../../utils/helpers';

const useEmailLogViewer = (emailLogId: string | undefined) => {
  const api = useApi();
  const { user } = useStoreContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [emailLog, setEmailLog] = useState<IEmailLogEntry | null>(null);

  const loadEmailLog = useCallback(async () => {
    if (Helpers.isNullOrWhiteSpace(emailLogId)) {
      setEmailLog(null);
      return;
    }

    setLoading(true);
    const endpoint = `/email-logs/${emailLogId}`;
    const apiResponse = await api.get<IEmailLogEntry>(endpoint);

    if (apiResponse.isSuccessStatusCode && apiResponse.response) {
      setEmailLog(apiResponse.response);
    } else {
      setEmailLog(null);
    }
    setLoading(false);
  }, [api, emailLogId]);

  const downloadEml = async () => {
    if (!emailLogId || !emailLog) return;

    try {
      const endpoint = `/email-logs/${emailLogId}/download/eml`;
      const response = await fetch(`${import.meta.env.VITE_APP_API_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${user.token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        let fileName = 'email.eml';

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename[*]?=['"]?([^'";]+)['"]?/i);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1];
          }
        } else {
          // Generate filename from email details
          const sanitizeFilename = (str: string) => {
            return str.replace(/[^a-z0-9]/gi, '_').substring(0, 50);
          };
          const dateStr = emailLog.sendDateUtc
            ? new Date(emailLog.sendDateUtc).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0];
          const subjectPart = sanitizeFilename(emailLog.subject || 'email');
          fileName = `${dateStr}_${subjectPart}.eml`;
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error downloading EML:', error);
    }
  };

  const downloadPdf = () => {
    if (!emailLog) return;

    // Create hidden iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow?.document;
    if (!iframeDoc) return;

    // Build HTML for printing
    const printHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Email - ${emailLog.subject}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            color: #333;
          }
          .email-header {
            border: 1px solid #ddd;
            padding: 15px;
            background-color: #f8f9fa;
            margin-bottom: 20px;
          }
          .email-header-row {
            margin-bottom: 8px;
          }
          .email-header-label {
            font-weight: bold;
            display: inline-block;
            width: 100px;
          }
          .email-body {
            border: 1px solid #ddd;
            padding: 15px;
          }
          @media print {
            body {
              margin: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="email-header">
          <h2 style="margin-top: 0;">Email Message</h2>
          <div class="email-header-row">
            <span class="email-header-label">From:</span>
            <span>${emailLog.from}</span>
          </div>
          <div class="email-header-row">
            <span class="email-header-label">To:</span>
            <span>${emailLog.recipients
              .filter((r) => r.type === 0)
              .map((r) => r.email)
              .join(', ')}</span>
          </div>
          ${
            emailLog.recipients.filter((r) => r.type === 1).length > 0
              ? `
          <div class="email-header-row">
            <span class="email-header-label">Cc:</span>
            <span>${emailLog.recipients
              .filter((r) => r.type === 1)
              .map((r) => r.email)
              .join(', ')}</span>
          </div>
          `
              : ''
          }
          <div class="email-header-row">
            <span class="email-header-label">Subject:</span>
            <span>${emailLog.subject}</span>
          </div>
          <div class="email-header-row">
            <span class="email-header-label">Date:</span>
            <span>${new Date(emailLog.sendDateUtc || emailLog.entryDateUtc).toLocaleString()}</span>
          </div>
        </div>
        <div class="email-body">
          ${emailLog.body}
        </div>
      </body>
      </html>
    `;

    iframeDoc.open();
    iframeDoc.write(printHtml);
    iframeDoc.close();

    // Wait for content to load, then print
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.print();
        // Clean up iframe after a delay
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }, 250);
    };
  };

  const backToSearch = () => {
    navigate('/email-logs');
  };

  useEffect(() => {
    void loadEmailLog();
  }, [emailLogId]); // Only depend on emailLogId, not loadEmailLog

  return {
    loading,
    emailLog,
    downloadEml,
    downloadPdf,
    backToSearch
  };
};

export default useEmailLogViewer;
